// Structural reference data for the MCP API documentation page (mcp-documentation-reference.vue).
// Mirrors mcp-server/src/tools/*.js as of writing - kept in sync BY HAND (no live introspection,
// see the plan's rationale: content needs hand-translation into 6 languages anyway, so an extra
// runtime fetch/auth dependency between the dashboard and mcp-server would only buy automatic sync
// on the structure, not the text - not worth the added complexity). If a tool's params change in
// mcp-server, this file and the matching i18n keys (dash.settings.mcp_docs.tools.<name>.*) need a
// manual update.
//
// Human-readable text (tool description, param descriptions, response format) is NOT stored here
// - it lives in the i18n locale files under `dash.settings.mcp_docs.tools.<tool>.*`, translated
// into all 6 languages. This file only holds language-independent structure: category, param
// name/type/required, and one realistic example arguments object per tool (reused identically for
// the curl/JS/Python renderings - only the transport wrapper differs, see mcp-code-example.vue).

export const TOOL_CATEGORIES = [
  { key: 'bots', icon: 'fa-solid fa-robot', tools: ['list_bots', 'list_groups', 'create_bot', 'update_bot'] },
  { key: 'posts', icon: 'fa-solid fa-file-pen', tools: ['list_posts', 'get_post', 'create_post', 'update_post', 'delete_post', 'assign_post', 'create_post_note'] },
  { key: 'labels', icon: 'fa-solid fa-tags', tools: ['list_labels', 'create_label', 'update_label', 'delete_label', 'set_post_labels'] },
  { key: 'moderation', icon: 'fa-solid fa-comments', tools: ['list_inbox_events', 'get_inbox_event', 'update_inbox_event'] },
  { key: 'reporting', icon: 'fa-solid fa-chart-line', tools: ['get_page_reporting'] },
  { key: 'sources', icon: 'fa-solid fa-rss', tools: ['list_sources', 'create_source', 'update_source'] },
  { key: 'users', icon: 'fa-solid fa-users', tools: ['list_users'] },
  { key: 'variables', icon: 'fa-solid fa-code', tools: ['list_variables', 'create_variable', 'update_variable', 'delete_variable', 'set_variable_value'] }
]

// Placeholder tokens used inside exampleArgs below - never real ids, deliberately obvious in the
// rendered code panel so nobody copy-pastes them as-is without substituting their own values.
const PAGE_ID = '<page_id>'
const PAGE_ID_2 = '<page_id_2>'
const BOT_ID = '<bot_id>'
const BOT_ID_2 = '<bot_id_2>'
const POST_ID = '<post_id>'
const USER_ID = '<user_id>'
const LABEL_ID = '<label_id>'
const SOURCE_ID = '<source_id>'
const VARIABLE_ID = '<variable_id>'
const EVENT_ID = '<event_id>'

// type: a short renderable type label for the params table (not a strict schema dump - arrays are
// noted as "array de X" via the i18n description instead of a separate sub-shape here).
export const TOOLS = {
  // --- bots ---------------------------------------------------------------
  list_bots: {
    category: 'bots',
    readOnly: true,
    params: [
      { name: 'query', type: 'object', required: false }
    ],
    exampleArgs: {}
  },
  list_groups: {
    category: 'bots',
    readOnly: true,
    params: [],
    exampleArgs: {}
  },
  create_bot: {
    category: 'bots',
    readOnly: false,
    params: [
      { name: 'page_id', type: 'string', required: true },
      { name: 'network', type: 'string', required: true },
      { name: 'fields', type: 'object', required: true }
    ],
    exampleArgs: { page_id: PAGE_ID, network: 'linkedin', fields: {} }
  },
  update_bot: {
    category: 'bots',
    readOnly: false,
    params: [
      { name: 'bot_id', type: 'string', required: true },
      { name: 'fields', type: 'object', required: true }
    ],
    exampleArgs: { bot_id: BOT_ID, fields: { name: 'MWtest1 default' } }
  },

  // --- posts ----------------------------------------------------------------
  list_posts: {
    category: 'posts',
    readOnly: true,
    params: [
      { name: 'page_ids', type: 'array<string>', required: true },
      { name: 'statuses', type: 'array<enum>', required: false },
      { name: 'types', type: 'array<enum>', required: false },
      { name: 'search', type: 'string', required: false },
      { name: 'start_date', type: 'string (ISO)', required: false },
      { name: 'end_date', type: 'string (ISO)', required: false },
      { name: 'limit', type: 'number', required: false },
      { name: 'cursor', type: 'string', required: false },
      { name: 'include_metrics', type: 'boolean', required: false }
    ],
    exampleArgs: { page_ids: [PAGE_ID], statuses: ['scheduled', 'posted'], limit: 20 }
  },
  get_post: {
    category: 'posts',
    readOnly: true,
    params: [
      { name: 'bot_id', type: 'string', required: true },
      { name: 'post_id', type: 'string', required: true }
    ],
    exampleArgs: { bot_id: BOT_ID, post_id: POST_ID }
  },
  create_post: {
    category: 'posts',
    readOnly: false,
    params: [
      { name: 'pages', type: 'array<object>', required: true },
      { name: 'content', type: 'object', required: false },
      { name: 'story', type: 'object', required: false },
      { name: 'scheduled_date', type: 'string (ISO)', required: false },
      { name: 'personalization_by_network', type: 'object', required: false },
      { name: 'personalization_by_page', type: 'object', required: false },
      { name: 'confirm_publish', type: 'boolean', required: false },
      { name: 'assign_to_user_ids', type: 'array<string>', required: false },
      { name: 'required_validation', type: 'boolean', required: false }
    ],
    exampleArgs: {
      pages: [{ page_id: PAGE_ID, bot_id: BOT_ID, network: 'linkedin' }],
      content: { text: 'Notre nouvelle offre est disponible dès aujourd’hui !' },
      scheduled_date: '2026-10-01T10:00:00.000Z'
    }
  },
  update_post: {
    category: 'posts',
    readOnly: false,
    params: [
      { name: 'pages', type: 'array<object>', required: true },
      { name: 'content', type: 'object (partial)', required: false },
      { name: 'story', type: 'object (partial)', required: false },
      { name: 'scheduled_date', type: 'string (ISO)', required: false },
      { name: 'personalization_by_network', type: 'object', required: false },
      { name: 'personalization_by_page', type: 'object', required: false },
      { name: 'confirm_publish', type: 'boolean', required: false },
      { name: 'assign_to_user_ids', type: 'array<string>', required: false },
      { name: 'required_validation', type: 'boolean', required: false }
    ],
    exampleArgs: {
      pages: [{ page_id: PAGE_ID, bot_id: BOT_ID, network: 'linkedin', post_id: POST_ID }],
      content: { text: 'Texte mis à jour.' }
    }
  },
  delete_post: {
    category: 'posts',
    readOnly: false,
    params: [
      { name: 'bot_id', type: 'string', required: true },
      { name: 'post_id', type: 'string', required: true },
      { name: 'delete_all_linked_pages', type: 'boolean', required: false }
    ],
    exampleArgs: { bot_id: BOT_ID, post_id: POST_ID, delete_all_linked_pages: false }
  },
  assign_post: {
    category: 'posts',
    readOnly: false,
    params: [
      { name: 'post_id', type: 'string', required: true },
      { name: 'bot_id', type: 'string', required: true },
      { name: 'page_id', type: 'string', required: true },
      { name: 'assign_to_user_ids', type: 'array<string>', required: false },
      { name: 'required_validation', type: 'boolean', required: false }
    ],
    exampleArgs: { post_id: POST_ID, bot_id: BOT_ID, page_id: PAGE_ID, assign_to_user_ids: [USER_ID] }
  },
  create_post_note: {
    category: 'posts',
    readOnly: false,
    params: [
      { name: 'bot_id', type: 'string', required: true },
      { name: 'post_id', type: 'string', required: true },
      { name: 'message', type: 'string', required: true },
      { name: 'author_name', type: 'string', required: true },
      { name: 'author_email', type: 'string', required: false },
      { name: 'apply_to_all_linked_pages', type: 'boolean', required: false }
    ],
    exampleArgs: { bot_id: BOT_ID, post_id: POST_ID, message: 'Visuel à valider avant publication.', author_name: 'Assistant IA' }
  },

  // --- labels -----------------------------------------------------------
  list_labels: {
    category: 'labels',
    readOnly: true,
    params: [],
    exampleArgs: {}
  },
  create_label: {
    category: 'labels',
    readOnly: false,
    params: [
      { name: 'name', type: 'string', required: true },
      { name: 'color', type: 'string (hex)', required: true },
      { name: 'icon', type: 'string', required: false }
    ],
    exampleArgs: { name: 'Urgent', color: '#6B1F4D' }
  },
  update_label: {
    category: 'labels',
    readOnly: false,
    params: [
      { name: 'label_id', type: 'string', required: true },
      { name: 'name', type: 'string', required: true },
      { name: 'color', type: 'string (hex)', required: true },
      { name: 'icon', type: 'string', required: false }
    ],
    exampleArgs: { label_id: LABEL_ID, name: 'Urgent', color: '#B64985' }
  },
  delete_label: {
    category: 'labels',
    readOnly: false,
    params: [
      { name: 'label_id', type: 'string', required: true }
    ],
    exampleArgs: { label_id: LABEL_ID }
  },
  set_post_labels: {
    category: 'labels',
    readOnly: false,
    params: [
      { name: 'bot_id', type: 'string', required: true },
      { name: 'post_id', type: 'string', required: true },
      { name: 'label_ids', type: 'array<string>', required: false },
      { name: 'add_label_ids', type: 'array<string>', required: false },
      { name: 'remove_label_ids', type: 'array<string>', required: false },
      { name: 'apply_to_all_linked_pages', type: 'boolean', required: false }
    ],
    exampleArgs: { bot_id: BOT_ID, post_id: POST_ID, add_label_ids: [LABEL_ID] }
  },

  // --- moderation -------------------------------------------------------
  list_inbox_events: {
    category: 'moderation',
    readOnly: true,
    params: [
      { name: 'page_ids', type: 'array<string>', required: false },
      { name: 'all_pages', type: 'boolean', required: false },
      { name: 'type', type: 'array<enum>', required: false },
      { name: 'status', type: 'array<enum>', required: false },
      { name: 'flag', type: 'boolean', required: false },
      { name: 'evaluation', type: 'array<enum>', required: false },
      { name: 'affected_to_user_ids', type: 'array<string>', required: false },
      { name: 'user_id', type: 'string', required: false },
      { name: 'from', type: 'string', required: false },
      { name: 'async_conversations', type: 'boolean', required: false }
    ],
    exampleArgs: { page_ids: [PAGE_ID], status: ['waiting'] }
  },
  get_inbox_event: {
    category: 'moderation',
    readOnly: true,
    params: [
      { name: 'page_id', type: 'string', required: true },
      { name: 'object_id', type: 'string', required: true }
    ],
    exampleArgs: { page_id: PAGE_ID, object_id: EVENT_ID }
  },
  update_inbox_event: {
    category: 'moderation',
    readOnly: false,
    params: [
      { name: 'event_id', type: 'string', required: true },
      { name: 'page_id', type: 'string', required: true },
      { name: 'status', type: 'enum', required: false },
      { name: 'evaluation', type: 'enum', required: false },
      { name: 'flag', type: 'boolean', required: false },
      { name: 'affected_to_user_ids', type: 'array<string>', required: false },
      { name: 'hide', type: 'boolean', required: false }
    ],
    exampleArgs: { event_id: EVENT_ID, page_id: PAGE_ID, status: 'treated' }
  },

  // --- reporting ----------------------------------------------------------
  get_page_reporting: {
    category: 'reporting',
    readOnly: true,
    params: [
      { name: 'page_id', type: 'string', required: true },
      { name: 'start_date', type: 'string (ISO)', required: true },
      { name: 'end_date', type: 'string (ISO)', required: true },
      { name: 'timezone_offset_minutes', type: 'number', required: false }
    ],
    exampleArgs: { page_id: PAGE_ID, start_date: '2026-08-01T00:00:00.000Z', end_date: '2026-09-01T00:00:00.000Z' }
  },

  // --- sources ------------------------------------------------------------
  list_sources: {
    category: 'sources',
    readOnly: true,
    params: [
      { name: 'query', type: 'object', required: false }
    ],
    exampleArgs: {}
  },
  create_source: {
    category: 'sources',
    readOnly: false,
    params: [
      { name: 'data', type: 'object', required: true }
    ],
    exampleArgs: { data: { url: 'https://exemple.com/flux.rss', name: 'Blog exemple' } }
  },
  update_source: {
    category: 'sources',
    readOnly: false,
    params: [
      { name: 'source_id', type: 'string', required: true },
      { name: 'data', type: 'object', required: true }
    ],
    exampleArgs: { source_id: SOURCE_ID, data: { name: 'Blog exemple (renommé)' } }
  },

  // --- users ----------------------------------------------------------------
  list_users: {
    category: 'users',
    readOnly: true,
    params: [],
    exampleArgs: {}
  },

  // --- variables ------------------------------------------------------------
  list_variables: {
    category: 'variables',
    readOnly: true,
    params: [],
    exampleArgs: {}
  },
  create_variable: {
    category: 'variables',
    readOnly: false,
    params: [
      { name: 'name', type: 'string', required: true }
    ],
    exampleArgs: { name: 'adresse' }
  },
  update_variable: {
    category: 'variables',
    readOnly: false,
    params: [
      { name: 'variable_id', type: 'string', required: true },
      { name: 'name', type: 'string', required: true }
    ],
    exampleArgs: { variable_id: VARIABLE_ID, name: 'adresse_siege' }
  },
  delete_variable: {
    category: 'variables',
    readOnly: false,
    params: [
      { name: 'variable_id', type: 'string', required: true }
    ],
    exampleArgs: { variable_id: VARIABLE_ID }
  },
  set_variable_value: {
    category: 'variables',
    readOnly: false,
    params: [
      { name: 'variable_id', type: 'string', required: true },
      { name: 'page_id', type: 'string', required: true },
      { name: 'value', type: 'string', required: true }
    ],
    exampleArgs: { variable_id: VARIABLE_ID, page_id: PAGE_ID, value: '12 rue de la Paix, 75002 Paris' }
  }
}

export const ALL_TOOL_NAMES = Object.keys(TOOLS)
